import React from "react";
import { useTranslation } from "react-i18next";
import { Iconfont } from "@eweadn-qiankun/shared-components";
import {
  sensorRotationDialLight,
  sensorRotationDialDark,
  sensorRotationMouseLight,
  sensorRotationMouseDark,
  sensorRotationScaleLight,
  sensorRotationScaleDark,
} from "@eweadn-qiankun/shared-assets";
import "./SensorRotation.scss";

const getTheme = () =>
  (document.documentElement.getAttribute("data-theme") || "light") as
    | "light"
    | "dark";

// 表盘尺寸与 rotatedial.svg viewBox 一致
const DIAL_WIDTH = 428;
const DIAL_HEIGHT = 300;
const CENTER_X = 215;
const CENTER_Y = 175;
const RADIUS = 126;
// 起始点（value=0）在表盘顶部 12 点钟：数学角 -90°
const ANGLE_AT_TOP = -90;
// 参数 1 度 = 真实圆周 4 度，即 30 对应 120°
const REAL_DEGREES_PER_UNIT = 4.5;

interface SensorRotationProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}

const SensorRotation: React.FC<SensorRotationProps> = ({
  value,
  onChange,
  min = -30,
  max = 30,
}) => {
  const { t } = useTranslation();
  const intervalRef = React.useRef<number | null>(null);
  const timeoutRef = React.useRef<number | null>(null);
  const valueRef = React.useRef(value);
  const dialRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [theme, setTheme] = React.useState<"light" | "dark">(getTheme);

  React.useEffect(() => {
    const observer = new MutationObserver(() => setTheme(getTheme()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const dialImg =
    theme === "dark" ? sensorRotationDialDark : sensorRotationDialLight;
  const mouseImg =
    theme === "dark" ? sensorRotationMouseDark : sensorRotationMouseLight;
  const scaleImg =
    theme === "dark" ? sensorRotationScaleDark : sensorRotationScaleLight;

  const handleDecrement = () => {
    onChange(Math.max(min, value - 1));
  };

  const handleIncrement = () => {
    onChange(Math.min(max, value + 1));
  };

  const startDecrement = () => {
    handleDecrement();
    timeoutRef.current = window.setTimeout(() => {
      intervalRef.current = window.setInterval(() => {
        const newValue = Math.max(min, valueRef.current - 1);
        valueRef.current = newValue;
        onChange(newValue);
      }, 100);
    }, 800);
  };

  const startIncrement = () => {
    handleIncrement();
    timeoutRef.current = window.setTimeout(() => {
      intervalRef.current = window.setInterval(() => {
        const newValue = Math.min(max, valueRef.current + 1);
        valueRef.current = newValue;
        onChange(newValue);
      }, 100);
    }, 800);
  };

  const stopAdjustment = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const updateValueFromClientPoint = React.useCallback(
    (clientX: number, clientY: number) => {
      if (!dialRef.current) return;
      const rect = dialRef.current.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const scaleX = DIAL_WIDTH / rect.width;
      const scaleY = DIAL_HEIGHT / rect.height;
      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;
      const deltaX = x - CENTER_X;
      const deltaY = y - CENTER_Y;
      const angleDeg = (Math.atan2(deltaY, deltaX) * 180) / Math.PI;
      // 真实圆周角相对顶部的偏移（-120°～120°），再换算为参数 -30～30
      let visualOffset = angleDeg - ANGLE_AT_TOP;
      if (visualOffset > 180) visualOffset -= 360;
      if (visualOffset < -180) visualOffset += 360;
      let paramAngle = visualOffset / REAL_DEGREES_PER_UNIT;
      paramAngle = Math.max(min, Math.min(max, paramAngle));
      const aligned = Math.round(paramAngle);
      onChange(aligned);
    },
    [min, max, onChange]
  );

  // 与 demo 一致：点按表盘/轨道即跳转到该角度，并进入拖动
  const handleDialPointerDown = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    e.preventDefault();
    setIsDragging(true);
    if ("touches" in e) {
      const touch = e.touches[0];
      if (touch) updateValueFromClientPoint(touch.clientX, touch.clientY);
    } else {
      updateValueFromClientPoint(e.clientX, e.clientY);
    }
  };

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      updateValueFromClientPoint(e.clientX, e.clientY);
    },
    [updateValueFromClientPoint]
  );

  const handleTouchMove = React.useCallback(
    (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      e.preventDefault();
      updateValueFromClientPoint(touch.clientX, touch.clientY);
    },
    [updateValueFromClientPoint]
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (!isDragging) return;
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleMouseUp);
    window.addEventListener("touchcancel", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
      window.removeEventListener("touchcancel", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleTouchMove]);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // 把手位置（表盘上）：参数 value 转为真实圆周角
  const visualDeg = ANGLE_AT_TOP + value * REAL_DEGREES_PER_UNIT;
  const angleRad = (visualDeg * Math.PI) / 180;
  const handleX = CENTER_X + RADIUS * Math.cos(angleRad);
  const handleY = CENTER_Y + RADIUS * Math.sin(angleRad);

  // 橙色弧：从 0° 到 value 对应的真实角度
  const startAngle = (ANGLE_AT_TOP * Math.PI) / 180;
  const endAngle = (visualDeg * Math.PI) / 180;
  const startX = CENTER_X + RADIUS * Math.cos(startAngle);
  const startY = CENTER_Y + RADIUS * Math.sin(startAngle);
  const endX = CENTER_X + RADIUS * Math.cos(endAngle);
  const endY = CENTER_Y + RADIUS * Math.sin(endAngle);
  const arcAngleDeg = value * REAL_DEGREES_PER_UNIT;
  const largeArc = Math.abs(arcAngleDeg) > 180 ? 1 : 0;
  const sweep = value > 0 ? 1 : 0;
  const arcD =
    value !== 0
      ? `M ${startX} ${startY} A ${RADIUS} ${RADIUS} 0 ${largeArc} ${sweep} ${endX} ${endY}`
      : "";

  return (
    <div
      className={`sensor-rotation-container ${isDragging ? "dragging" : ""}`}
    >
      <button
        className="sensor-rotation-btn sensor-rotation-btn-left"
        onMouseDown={startDecrement}
        onMouseUp={stopAdjustment}
        onMouseLeave={stopAdjustment}
        title={t("mouse.parameter.rotateLeft")}
      >
        <Iconfont icon="leftaction" className="rotation-arrow-icon" />
      </button>
      <div
        className="sensor-rotation-dial"
        ref={dialRef}
        onMouseDown={handleDialPointerDown}
        onTouchStart={handleDialPointerDown}
      >
        <img
          src={dialImg}
          alt=""
          className="sensor-rotation-dial-image"
          draggable={false}
        />
        <svg
          className="sensor-rotation-track"
          viewBox={`0 0 ${DIAL_WIDTH} ${DIAL_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden
        >
          <defs>
            <filter
              id="sensor-rotation-arc-blur"
              filterUnits="userSpaceOnUse"
              x="0"
              y="0"
              width={DIAL_WIDTH}
              height={DIAL_HEIGHT}
            >
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
            </filter>
          </defs>
          {value !== 0 && (
            <>
              {/* before：底层模糊光晕 */}
              <path
                className="sensor-rotation-arc-before"
                d={arcD}
                fill="none"
                stroke="rgba(255, 80, 25, 0.4)"
                strokeWidth="44"
                strokeLinecap="butt"
                filter="url(#sensor-rotation-arc-blur)"
              />
              {/* after：上层实色轨道 */}
              <path
                className="sensor-rotation-arc"
                d={arcD}
                fill="none"
                stroke="#FF7347"
                strokeWidth="40"
                strokeLinecap="butt"
              />
            </>
          )}
        </svg>
        <img
          src={scaleImg}
          alt=""
          className="sensor-rotation-scale"
          draggable={false}
        />
        <div
          className="sensor-rotation-indicator"
          style={{
            left: `${(handleX / DIAL_WIDTH) * 100}%`,
            top: `${(handleY / DIAL_HEIGHT) * 100}%`,
            transform: `translate(-50%, -50%) rotate(${visualDeg + 90}deg)`,
          }}
        />
        <div className="sensor-rotation-center">
          <div className="sensor-rotation-mouse-wrap">
            <div
              className="sensor-rotation-mouse-rotate"
              style={{ transform: `rotate(${visualDeg + 90}deg)` }}
            >
              <img
                src={mouseImg}
                alt=""
                className="sensor-rotation-mouse-image"
                draggable={false}
              />
            </div>
            <span className="sensor-rotation-value">
              {Math.abs(Math.round(value))}°
            </span>
          </div>
        </div>
      </div>
      <button
        className="sensor-rotation-btn sensor-rotation-btn-right"
        onMouseDown={startIncrement}
        onMouseUp={stopAdjustment}
        onMouseLeave={stopAdjustment}
        title={t("mouse.parameter.rotateRight")}
      >
        <Iconfont icon="rightaction" className="rotation-arrow-icon" />
      </button>
    </div>
  );
};

export default SensorRotation;
